import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Input } from 'antd'
import { IInputText } from './interface'
import { useDebounce } from '../../hooks/useDebounce/useDebounce'
import {
  isMaskRegExp,
  validateWithMaskRegex,
  parseDisplayFormatValue,
  formatWithDisplayPattern,
  getPlaceholderFromFormat,
} from './mask-utils'

function InputText({ value, onChange, schema, UISchema }: IInputText) {
  // options.disabled в форме → DisabledFieldRenderer (карточки).
  // options.inputDisabled — обычный disabled Input (модалки и т.п.).
  const disabled = useMemo(
    () =>
      Boolean(
        schema?.options?.inputDisabled ||
          UISchema?.options?.inputDisabled ||
          UISchema?.options?.disabled,
      ),
    [schema, UISchema],
  )
  const style = UISchema?.style
  const mask = schema?.options?.mask ?? UISchema?.options?.mask
  const displayFormat = schema?.options?.displayFormat ?? UISchema?.options?.displayFormat
  const [localValue, setLocalValue] = useState<string>(value ?? '')

  const maskAsRegex = useMemo(() => {
    if (!mask || !isMaskRegExp(mask)) return null
    try {
      return new RegExp(mask)
    } catch {
      return null
    }
  }, [mask])

  const isDisplayFormatMode = displayFormat != null && typeof displayFormat === 'string'
  const isRegexMode = !isDisplayFormatMode && mask != null && maskAsRegex != null

  useEffect(() => {
    if (value !== undefined) {
      const normalized = isDisplayFormatMode
        ? parseDisplayFormatValue(String(value ?? ''), displayFormat)
        : String(value ?? '')
      setLocalValue(normalized)
    }
  }, [value, displayFormat, isDisplayFormatMode])

  const options = useMemo(() => {
    const isTextArea =
      schema?.options?.type === 'textarea' || UISchema?.options?.type === 'textarea'
    return {
      type: isTextArea ? 'textarea' : 'text',
      minRows: UISchema?.options?.minRows ? UISchema.options?.minRows : 1,
      maxRows: UISchema?.options?.maxRows ? UISchema.options?.maxRows : 1,
    }
  }, [schema, UISchema])

  const displayValue = useMemo(() => {
    if (!isDisplayFormatMode) return localValue
    return formatWithDisplayPattern(localValue, displayFormat)
  }, [localValue, displayFormat, isDisplayFormatMode])

  const maskValid = useMemo(() => {
    if (isDisplayFormatMode && maskAsRegex != null) {
      return validateWithMaskRegex(localValue, maskAsRegex)
    }
    if (isRegexMode && maskAsRegex != null) {
      return validateWithMaskRegex(localValue, maskAsRegex)
    }
    return true
  }, [isDisplayFormatMode, isRegexMode, localValue, maskAsRegex])

  const debouncedOnChange = useDebounce((newValue: string) => {
    if (onChange) {
      onChange(newValue)
    }
  }, 1000)

  const onChangeLocal = useCallback(
    e => {
      const raw = e.target.value
      if (isDisplayFormatMode) {
        const parsed = parseDisplayFormatValue(raw, displayFormat)
        setLocalValue(parsed)
        debouncedOnChange(parsed)
        return
      }
      if (isRegexMode && maskAsRegex != null) {
        const allowed = raw === '' || maskAsRegex.test(raw)
        if (!allowed) return
      }
      setLocalValue(raw)
      debouncedOnChange(raw)
    },
    [debouncedOnChange, isDisplayFormatMode, displayFormat, isRegexMode, maskAsRegex],
  )

  const onBlur = useCallback(() => {
    if (onChange && String(localValue) !== String(value)) {
      onChange(localValue)
    }
  }, [onChange, localValue, value])

  const statusFromOptions = schema?.options?.status ?? UISchema?.options?.status
  const status =
    statusFromOptions ||
    ((isRegexMode || isDisplayFormatMode) && !maskValid && localValue !== '' ? 'error' : undefined)
  const inputValue = isDisplayFormatMode ? displayValue : localValue
  const placeholder =
    schema?.options?.placeholder ??
    UISchema?.options?.placeholder ??
    (isDisplayFormatMode ? getPlaceholderFromFormat(displayFormat) : undefined)

  if (options.type === 'textarea') {
    return (
      <Input.TextArea
        autoSize={{ minRows: options.minRows, maxRows: options.maxRows }}
        value={inputValue}
        onChange={onChangeLocal}
        onBlur={onBlur}
        disabled={disabled}
        style={style}
        status={status}
        placeholder={placeholder}
      />
    )
  }
  return (
    <Input
      value={inputValue}
      onChange={onChangeLocal}
      onBlur={onBlur}
      disabled={disabled}
      style={style}
      status={status}
      placeholder={placeholder}
    />
  )
}

export default React.memo(InputText)
